// Pure player-seat / settlement ledger helpers.
//
// Chipless identity is the Firebase anonymous UID (the key under rooms/{code}/players).
// A localStorage session remembers that key across refresh. Neither of those
// survives a new phone, a cleared browser, or leaveRoom() deleting the node —
// which is why a player who left could not get their stack back.
//
// These helpers never talk to Firebase. They keep chips conserved:
//   sum(totalInvestment) === sum(live stacks + live pot contributions + cashed-out amounts)
//   sum(net results) === 0
// pokerLogic.calculateSettlements is unchanged; we pass it the right stacks.

// Joins need no host approval (in-person game). Every join is seated at once:
// straight into the lobby, or mid-game as a "Next hand" seat. The host keeps
// the table clean by removing players instead (no block list: a removed
// player may come back like any joiner). `isApproved: true` is still
// written on every record for older clients that may be open mid-rollout;
// nothing in this app reads it.

/** Seat key plus that seat's controllerUid (reclaim on a new phone). */
export function hostControllerUids(players, hostId) {
  const uids = new Set();
  if (hostId) uids.add(hostId);
  const seat = hostId ? players?.[hostId] : null;
  if (seat?.id) uids.add(seat.id);
  if (seat?.controllerUid) uids.add(seat.controllerUid);
  return uids;
}

/** UI host check. `playerId` is the seat id; optional `players` also matches controllerUid. */
export function isViewerHost(playerId, hostId, players) {
  if (!playerId || !hostId) return false;
  if (playerId === hostId) return true;
  return !!players && hostControllerUids(players, hostId).has(playerId);
}

/** Host writes: caller must be the host seat key or that seat's controllerUid. */
export function hostActionAllowed(roomHostId, callerUid, claimedHostId, players) {
  if (!callerUid || !roomHostId) return false;
  const allowed = hostControllerUids(players, roomHostId);
  if (!allowed.has(callerUid)) return false;
  if (claimedHostId && !allowed.has(claimedHostId)) return false;
  return true;
}

/** Auto-handoff after the host's presence has been disconnected this long. */
/** 2 minutes: checking a text or switching apps must never cost anyone host. */
export const HOST_DISCONNECT_MS = 2 * 60 * 1000;

/** Numeric lastSeen only — an unresolved `{.sv:"timestamp"}` is not elapsed time. */
export function presenceLastSeenMs(lastSeen) {
  return typeof lastSeen === 'number' && Number.isFinite(lastSeen) ? lastSeen : null;
}

/** Ms left before auto-handoff. Unknown lastSeen waits the full window. */
export function msUntilHostHandoff(presence, now, minMs = HOST_DISCONNECT_MS) {
  const lastSeen = presenceLastSeenMs(presence?.lastSeen);
  if (lastSeen == null) return minMs;
  return Math.max(0, minMs - (now - lastSeen));
}

/**
 * Next seated player clockwise from the host whose presence is connected.
 * Table order is joinedAt. Skips the current host and anyone not explicitly
 * connected — a missing presence is not "connected".
 */
export function nextConnectedHostId(players, hostId) {
  const seated = seatedPlayers(players);
  if (seated.length === 0) return null;
  const hostIndex = seated.findIndex((p) => p.id === hostId);
  const start = hostIndex >= 0 ? hostIndex + 1 : 0;
  for (let i = 0; i < seated.length; i += 1) {
    const p = seated[(start + i) % seated.length];
    if (p.id !== hostId && p.presence?.connected === true) return p.id;
  }
  return null;
}

/**
 * One write for hostId (seat key) and every isHost flag. Make host and a
 * later auto-handoff must both call this so nothing is half-host.
 */
export function planTransferHost(roomData, newHostSeatId, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  if (roomData.status === 'ENDED') {
    return { ok: false, reason: 'This game has already ended.' };
  }
  const currentHostId = roomData.hostId;
  if (!newHostSeatId || newHostSeatId === currentHostId) {
    return { ok: false, reason: 'Pick another player' };
  }
  const target = roomData.players?.[newHostSeatId];
  if (!isSeated(target)) {
    return { ok: false, reason: 'Player not found' };
  }

  const updates = {
    hostId: newHostSeatId,
    [`players/${newHostSeatId}/isHost`]: true,
    // A deliberate transfer ends any pending auto-return claim.
    originalHostId: null,
    lastActivity: now,
  };
  Object.values(roomData.players || {}).forEach((p) => {
    if (p?.id && p.id !== newHostSeatId && (p.isHost || p.id === currentHostId)) {
      updates[`players/${p.id}/isHost`] = false;
    }
  });
  return { ok: true, name: target.name, updates };
}

/**
 * Host disconnected long enough, and someone else is connected: hand the
 * table to the next connected seat. Callers re-read the room and pass the
 * hostId they observed so a second client aborts after the first write.
 */
export function planAutoHostHandoff(roomData, { expectedHostId, now = Date.now(), minMs = HOST_DISCONNECT_MS } = {}) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  if (roomData.status === 'ENDED') {
    return { ok: false, reason: 'This game has already ended.' };
  }
  const hostId = roomData.hostId;
  if (expectedHostId && hostId !== expectedHostId) {
    return { ok: false, reason: 'already-transferred' };
  }
  const host = hostId ? roomData.players?.[hostId] : null;
  if (!host || host.presence?.connected !== false) {
    return { ok: false, reason: 'host-connected' };
  }
  if (msUntilHostHandoff(host.presence, now, minMs) > 0) {
    return { ok: false, reason: 'too-soon' };
  }
  const nextId = nextConnectedHostId(roomData.players, hostId);
  if (!nextId) return { ok: false, reason: 'no-successor' };
  const plan = planTransferHost(roomData, nextId, now);
  if (!plan.ok) return plan;
  // Remember who hosted before the drop so they get the table back when
  // they reconnect. Keep the first original host across chained handoffs.
  const originalHostId = roomData.originalHostId || hostId;
  return {
    ...plan,
    updates: { ...plan.updates, originalHostId },
    originalHostId,
    fromId: hostId,
    fromName: host.name,
    nextHostId: nextId,
  };
}

/**
 * The host that auto-handoff moved away from is connected again: give the
 * table back. Only the returning host's own device runs this, and it is
 * idempotent (no-op once hostId is theirs / originalHostId is cleared).
 */
export function planHostReturn(roomData, playerId, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  if (roomData.status === 'ENDED') return { ok: false, reason: 'ended' };
  const originalHostId = roomData.originalHostId;
  if (!originalHostId || originalHostId !== playerId) {
    return { ok: false, reason: 'not-original-host' };
  }
  const me = roomData.players?.[playerId];
  if (roomData.hostId === playerId) {
    return { ok: true, noop: true, updates: { originalHostId: null } };
  }
  if (!isSeated(me) || me.presence?.connected !== true) {
    return { ok: false, reason: 'not-back' };
  }
  const plan = planTransferHost(roomData, playerId, now);
  if (!plan.ok) return plan;
  return { ...plan, updates: { ...plan.updates, originalHostId: null } };
}

export function isLeft(player) {
  return player?.status === 'left';
}

/**
 * Seated at the table for dealing, blinds, and turn order. Requires an id so a
 * stray presence-only node (an onDisconnect landing after a lobby removal) is
 * never drawn as a seat.
 */
export function isSeated(player) {
  return !!(player && player.id && player.status !== 'left');
}

/**
 * Folded in the current hand (still at the table, just out of this pot).
 * A rejoin / mid-game sit-out uses isActive:false — that must not look folded.
 */
export function isFoldedThisHand(player) {
  return !!(player && player.hasFolded && player.isActive);
}

/**
 * Seated player who tapped Sit out and is skipped for dealing (or paying
 * blinds then auto-folding, when that host setting is on). Same seat/stack;
 * still in settlement.
 */
export function isAway(player) {
  return !!(isSeated(player) && player.away === true);
}

/**
 * Copy Away / I'm back / missed-blind flags from a newer RTDB snapshot onto
 * a local players list. Action and deal writes replace the whole player node,
 * so a stale client would otherwise put someone back on Away after I'm back.
 * `preserveId` keeps that seat's local flags (the actor who just sat out or
 * auto-folded).
 */
export function mergeLiveAwayFlags(players, livePlayers, { preserveId } = {}) {
  const liveMap = livePlayers && typeof livePlayers === 'object' && !Array.isArray(livePlayers)
    ? livePlayers
    : Object.fromEntries((livePlayers || []).filter((p) => p?.id).map((p) => [p.id, p]));
  const list = Array.isArray(players) ? players : Object.values(players || {});
  return list.map((p) => {
    if (!p?.id || p.id === preserveId) return p;
    const live = liveMap[p.id];
    if (!live) return p;
    return {
      ...p,
      away: live.away === true,
      sitOutPending: live.sitOutPending === true,
      missedSbCount: live.missedSbCount > 0 ? live.missedSbCount : 0,
      missedBbCount: live.missedBbCount > 0 ? live.missedBbCount : 0,
      missedBlindPostCents: live.missedBlindPostCents > 0 ? live.missedBlindPostCents : 0,
    };
  });
}

/**
 * Confirmed Sit out while still holding cards this hand. Stays in the pot;
 * auto-checks or auto-folds on their turns; becomes Away on the next deal
 * (or as soon as they fold).
 */
export function isSitOutPending(player) {
  return !!(isSeated(player) && player.sitOutPending === true && player.away !== true);
}

/** Still in this pot with cards (or all-in). Between-hands / folded is not. */
export function holdsCardsThisHand(player, winnerInfo) {
  if (!isSeated(player)) return false;
  if (winnerInfo && Array.isArray(winnerInfo.winnerIds) && winnerInfo.winnerIds.length > 0) {
    return false;
  }
  if (player.hasFolded) return false;
  if (player.isActive === false) return false;
  return true;
}

export function sitOutAutoAction(toCall) {
  return (toCall || 0) > 0
    ? { type: 'FOLD', auto: true }
    : { type: 'CALL', auto: true };
}

/**
 * Any client fires check/fold for this seat: pending Sit out, or Away in
 * pay-blinds mode who is still in this hand.
 */
export function needsSitOutAutoAction(player) {
  if (!isSeated(player)) return false;
  if (player.hasFolded || player.isActive === false) return false;
  if (player.isAllIn) return false;
  return isSitOutPending(player) || isAway(player);
}

/** Seated with no chips. All-in at $0 during a live hand is still chipless. */
export function isChipless(player) {
  return !!(isSeated(player) && (player.stack || 0) <= 0);
}

/**
 * Busted and sitting this hand out (skipped for blinds and dealing).
 * Distinct from a Next-hand joiner (`isActive: false` with chips) and from a
 * live all-in at $0 (`isActive: true`).
 */
export function isSpectatingOut(player) {
  return !!(isChipless(player) && player.isActive === false && !isAway(player));
}

/**
 * Pending phone-rebuy rows for the host card. Missing seats and leftover
 * nodes after a leave are dropped. Oldest request first.
 */
export function pendingRebuyList(rebuyRequests, players) {
  const map = players && typeof players === 'object' && !Array.isArray(players)
    ? players
    : Object.fromEntries((players || []).filter((p) => p?.id).map((p) => [p.id, p]));
  return Object.entries(rebuyRequests || {})
    .filter(([id, req]) => {
      if (!req || !Number.isFinite(req.amount) || req.amount <= 0) return false;
      return isSeated(map[id]);
    })
    .map(([playerId, req]) => ({
      playerId,
      name: map[playerId]?.name || '',
      amount: req.amount,
      requestedAt: req.requestedAt || 0,
    }))
    .sort((a, b) => (a.requestedAt || 0) - (b.requestedAt || 0) || String(a.playerId).localeCompare(String(b.playerId)));
}

/** Seat chrome: pending sit-out, Away, chipless Out, Next-hand joiner, or live. */
export function seatPresence(player) {
  if (isSitOutPending(player)) return 'pending';
  if (isAway(player)) return 'away';
  if (isSpectatingOut(player)) return 'out';
  if (isWaitingNextHand(player)) return 'next';
  return 'live';
}

/**
 * Seated, but sitting this hand out (mid-game join, reclaim, or Away).
 * Distinct from folded (isActive) and left. Away uses the same dashed seat
 * tone with an "Away" tag instead of "Next hand".
 */
export function isWaitingNextHand(player) {
  return !!(isSeated(player) && player.isActive === false);
}

/**
 * Can be picked as a showdown winner. Seated and not folded.
 * `isActive !== false` so a remount from RTDB that omitted `isActive: true`
 * still counts — a truthy check hid Declare Winner after a host refresh.
 * Next-hand sit-outs stay out (`isActive: false`).
 */
export function isShowdownEligible(player) {
  return !!(isSeated(player) && !player.hasFolded && player.isActive !== false);
}

export function isDisconnected(player) {
  return player?.presence?.connected === false;
}

/**
 * A seat another device can take over. Own UID can always reclaim.
 * Anyone else can only reclaim a seat that has been cashed out or whose
 * presence is explicitly disconnected — never a still-connected player,
 * and never a missing presence record (actions used to strip presence,
 * so "no presence" must not mean "up for grabs").
 * A disconnected host seat is reclaimable by name like any other; if the
 * table already handed host off, they come back as a regular player.
 */
export function isReclaimable(player, { requesterUid } = {}) {
  // A presence-only node (no id) is not a seat — nothing to reclaim.
  if (!player || !player.id) return false;
  const isOwner =
    !!requesterUid &&
    (player.id === requesterUid || player.controllerUid === requesterUid);
  if (isOwner) return true;
  if (player.presence?.connected === true) return false;
  return player.status === 'left' || player.presence?.connected === false;
}

export function occupancyCount(players) {
  return Object.values(players || {}).filter((p) => p && p.id && p.status !== 'left').length;
}

export function findPlayerByName(players, name) {
  const needle = String(name || '').trim().toLowerCase();
  if (!needle) return null;
  return Object.values(players || {}).find(
    (p) => p && String(p.name || '').trim().toLowerCase() === needle
  ) || null;
}

/** Seat this device already owns (key or controllerUid). */
export function findOwnSeat(players, uid) {
  if (!uid) return null;
  const byKey = players?.[uid];
  if (byKey?.id) return byKey;
  return Object.values(players || {}).find(
    (p) => p && p.id && p.controllerUid === uid
  ) || null;
}

export function vacatedSeats(players, requesterUid) {
  return Object.values(players || {})
    .filter((p) => p && p.id && isReclaimable(p, { requesterUid }))
    .sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0));
}

/** Seated players whose presence is explicitly disconnected (host rejoin-link picker). */
export function disconnectedSeats(players) {
  return seatedPlayers(players)
    .filter((p) => isDisconnected(p))
    .sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0));
}

const REJOIN_TOKEN_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789';

/** Short one-time token for a host-issued `/r/:code/:token` seat link. */
export function mintRejoinToken(length = 6) {
  const n = Math.max(4, Math.min(12, length | 0));
  let out = '';
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint8Array(n);
    crypto.getRandomValues(buf);
    for (let i = 0; i < n; i += 1) {
      out += REJOIN_TOKEN_ALPHABET[buf[i] % REJOIN_TOKEN_ALPHABET.length];
    }
    return out;
  }
  for (let i = 0; i < n; i += 1) {
    out += REJOIN_TOKEN_ALPHABET[Math.floor(Math.random() * REJOIN_TOKEN_ALPHABET.length)];
  }
  return out;
}

export function findSeatByRejoinToken(players, token) {
  const tok = String(token || '').trim();
  if (!tok) return null;
  return Object.values(players || {}).find((p) => p && p.id && p.rejoinToken === tok) || null;
}

/**
 * Host mints (or replaces) a one-time token on a disconnected seated player.
 * A new token invalidates any earlier link for that seat.
 */
export function planIssueRejoinToken(roomData, playerId, token, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  if (roomData.status === 'ENDED') {
    return { ok: false, reason: 'This game has already ended.' };
  }
  const player = roomData.players?.[playerId];
  if (!isSeated(player)) {
    return { ok: false, reason: 'That player is not at the table' };
  }
  if (player.presence?.connected === true) {
    return { ok: false, reason: 'That player is already connected.' };
  }
  const tok = String(token || '').trim();
  if (!tok) return { ok: false, reason: 'Missing token' };
  return {
    ok: true,
    token: tok,
    player,
    updates: {
      [`players/${playerId}/rejoinToken`]: tok,
      lastActivity: now,
    },
  };
}

export function seatedPlayers(players) {
  return Object.values(players || {})
    .filter(isSeated)
    .sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0));
}

/**
 * The next hand needs at least two seated players with chips who will
 * actually play. Away and pending-sit-out seats keep their stack but do
 * not count. Busted $0 seats (spectating) are skipped — they do not block
 * a 3+ player deal, and they cannot make a 1-player heads-up deal legal.
 */
export function canDealNextHand(players) {
  return seatedPlayers(players).filter((p) => (
    (p.stack || 0) > 0 && !isAway(p) && !isSitOutPending(p)
  )).length >= 2;
}

export function dealBlockedByAway(players) {
  const seated = seatedPlayers(players);
  const withChips = seated.filter((p) => (p.stack || 0) > 0);
  if (withChips.length < 2) return false;
  return !canDealNextHand(players);
}

/**
 * Pending sit-out becomes Away once they fold, go inactive, or the hand
 * is awarded. Does not mutate.
 */
export function finalizeSitOutFlags(players, gameState) {
  const handOver = Array.isArray(gameState?.winnerInfo?.winnerIds)
    && gameState.winnerInfo.winnerIds.length > 0;
  const asArray = Array.isArray(players);
  const list = asArray ? players : Object.values(players || {});
  const nextList = list.map((p) => {
    if (!p?.sitOutPending || p.away) return p;
    if (p.hasFolded || p.isActive === false || handOver) {
      return {
        ...p,
        sitOutPending: false,
        away: true,
        isActive: false,
        hasFolded: true,
      };
    }
    return p;
  });
  if (asArray) return nextList;
  const obj = {};
  nextList.forEach((p) => {
    if (p?.id) obj[p.id] = p;
  });
  return obj;
}

/**
 * Sit out. Holding cards: pending (stay in, auto check/fold). Already
 * folded or between hands: Away immediately. Stack unchanged.
 */
export function applySitOut(players, playerId, { winnerInfo } = {}) {
  const player = players?.[playerId];
  if (!isSeated(player)) return { ok: false, reason: 'Player not found' };
  if (isAway(player) && !player.sitOutPending) {
    return { ok: true, alreadyAway: true, players: clonePlayers(players), player: { ...player } };
  }

  let next;
  let pending = false;
  if (holdsCardsThisHand(player, winnerInfo)) {
    next = { ...player, sitOutPending: true, away: false };
    pending = true;
  } else {
    next = {
      ...player,
      away: true,
      sitOutPending: false,
      isActive: false,
      hasFolded: true,
    };
  }
  const updated = clonePlayers(players);
  updated[playerId] = next;
  return { ok: true, pending, players: updated, player: next };
}

/**
 * I'm back. Pending: cancel and stay in this hand. Away: same seat and
 * stack, dealt in next hand (missed-blind counters stay for the deal).
 */
export function applyImBack(players, playerId) {
  const player = players?.[playerId];
  if (!isSeated(player)) return { ok: false, reason: 'Player not found' };
  if (isSitOutPending(player)) {
    const next = { ...player, sitOutPending: false };
    const updated = clonePlayers(players);
    updated[playerId] = next;
    return { ok: true, cancelledPending: true, players: updated, player: next };
  }
  if (!isAway(player)) {
    return { ok: true, alreadyBack: true, players: clonePlayers(players), player: { ...player } };
  }

  const next = {
    ...player,
    away: false,
    sitOutPending: false,
    isActive: false,
    hasFolded: true,
  };
  const updated = clonePlayers(players);
  updated[playerId] = next;
  return { ok: true, waitingNext: true, players: updated, player: next };
}

export function parkedPlayers(players) {
  return Object.values(players || {}).filter((p) => p && !isSeated(p));
}

/** Remaining chips this player takes with them (not chips already in the pot). */
export function effectiveStack(player) {
  if (!player) return 0;
  if (player.status === 'left') return player.cashedOut ?? player.stack ?? 0;
  return player.stack || 0;
}

/**
 * Players that belong in who-owes-who: every real seat, including players who
 * left or were removed mid-game (their frozen stack is what they leave with). A stray
 * presence-only node has no id and no money, so it is dropped.
 */
export function settlementEntries(players) {
  return Object.values(players || {})
    .filter((p) => p && p.id)
    .map((p) => ({
      ...p,
      stack: effectiveStack(p),
      totalInvestment: p.totalInvestment || 0,
    }));
}

/** Chips this player has in the unfinished hand (pot + current street). */
export function uncollectedHandChips(player) {
  if (!player) return 0;
  // Street bets are already counted in totalPotContribution.
  return player.totalPotContribution || 0;
}

export function hasUnfinishedHandChips(players) {
  return Object.values(players || {}).some((p) => uncollectedHandChips(p) > 0);
}

/**
 * Void an unfinished hand: return each player's uncollected pot/street chips
 * to their stack (or cashedOut). Does not award a winner. Does not mutate.
 */
export function voidUnfinishedHand(players) {
  const next = clonePlayers(players);
  let returned = 0;
  Object.keys(next).forEach((id) => {
    const player = next[id];
    const chips = uncollectedHandChips(player);
    if (chips <= 0) {
      next[id] = {
        ...player,
        currentRoundBet: 0,
        totalPotContribution: 0,
      };
      return;
    }
    returned += chips;
    if (player.status === 'left') {
      const stack = (player.cashedOut ?? player.stack ?? 0) + chips;
      next[id] = {
        ...player,
        stack,
        cashedOut: stack,
        currentRoundBet: 0,
        totalPotContribution: 0,
      };
    } else {
      next[id] = {
        ...player,
        stack: (player.stack || 0) + chips,
        currentRoundBet: 0,
        totalPotContribution: 0,
      };
    }
  });
  return { players: next, returned, voided: returned > 0 };
}

export function ledgerTotals(players) {
  const list = Object.values(players || {});
  let buyIns = 0;
  let live = 0;
  let cashed = 0;
  for (const p of list) {
    buyIns += p.totalInvestment || 0;
    if (p.status === 'left') {
      cashed += p.cashedOut ?? p.stack ?? 0;
      // Bets they already put in this hand are still in the pot.
      live += p.totalPotContribution || 0;
    } else {
      live += (p.stack || 0) + (p.totalPotContribution || 0);
    }
  }
  const chips = live + cashed;
  const netSum = chips - buyIns;
  return {
    buyIns,
    chips,
    liveStacks: live,
    cashedOut: cashed,
    netSum,
    balanced: netSum === 0,
  };
}

function clonePlayers(players) {
  const next = {};
  Object.entries(players || {}).forEach(([id, p]) => {
    next[id] = { ...p };
  });
  return next;
}

/**
 * Cash out a seated player. Remaining stack is frozen on the player record
 * (cashedOut) so settlement still sees them. Chips already in the pot stay
 * there. Does not mutate `players`.
 *
 * All-in in a live hand is rejected: those chips are still contestable.
 */
export function applyCashOut(players, playerId, now = Date.now()) {
  const player = players?.[playerId];
  if (!player) return { ok: false, reason: 'Player not found' };
  if (player.isHost) {
    return { ok: false, reason: 'Host leaving ends the room' };
  }
  if (player.status === 'left') {
    return { ok: true, alreadyLeft: true, players: clonePlayers(players), player: { ...player } };
  }
  if (player.isAllIn && player.isActive && !player.hasFolded) {
    return { ok: false, reason: 'all-in' };
  }

  const othersLive = Object.values(players).filter(
    (p) => p.id !== playerId && p.isActive && !p.hasFolded
  );
  const lastActiveLeaving =
    othersLive.length === 0 && player.isActive && !player.hasFolded;

  let stack = player.stack || 0;
  let totalPotContribution = player.totalPotContribution || 0;
  let currentRoundBet = player.currentRoundBet || 0;
  if (lastActiveLeaving) {
    // Nobody else is in the hand — they pick up their own pot chips.
    stack += totalPotContribution;
    totalPotContribution = 0;
    currentRoundBet = 0;
  }

  const next = {
    ...player,
    stack,
    totalPotContribution,
    currentRoundBet,
    hasFolded: true,
    isActive: false,
    status: 'left',
    cashedOut: stack,
    leftAt: now,
  };

  const updated = clonePlayers(players);
  updated[playerId] = next;
  return {
    ok: true,
    players: updated,
    player: next,
    wasInHand: !!(player.isActive && !player.hasFolded),
    lastActiveLeaving,
  };
}

/**
 * Put a vacated player back in the game without a new buy-in.
 * A leftover cash-out sits out the current hand and is dealt the next one.
 */
export function applyReclaim(players, playerId, { controllerUid, now, viaToken } = {}) {
  const player = players?.[playerId];
  if (!player) return { ok: false, reason: 'Seat not found' };
  if (viaToken) {
    if (!player.id || player.presence?.connected === true) {
      return { ok: false, reason: 'That player is still at the table' };
    }
  } else if (!isReclaimable(player, { requesterUid: controllerUid })) {
    return { ok: false, reason: 'That player is still at the table' };
  }

  const wasLeft = player.status === 'left';
  const next = {
    ...player,
    isApproved: true,
    status: 'active',
    controllerUid: controllerUid || player.controllerUid || player.id,
    reclaimedAt: now || Date.now(),
    leftAt: null,
    cashedOut: null,
    rejoinToken: null,
    ...(wasLeft
      ? {
          isActive: false,
          hasFolded: true,
        }
      : {}),
  };

  const updated = clonePlayers(players);
  updated[playerId] = next;
  return { ok: true, players: updated, player: next, wasLeft };
}

/**
 * Brand-new joiner — seated immediately, no host approval. In the lobby they
 * are simply on the list. Mid-game (`playing`) they sit out the current hand
 * as a "Next hand" seat and resetForNewHand deals them in. Buy-in is recorded
 * now so settlement stays balanced the moment they sit down.
 */
export function applyJoin(players, newPlayer, { maxPlayers = 10, now, playing = false } = {}) {
  if (!newPlayer?.id) return { ok: false, reason: 'Missing player' };
  // A stray presence-only node under this uid (no id) is not a seat; the
  // join overwrites it.
  if (players?.[newPlayer.id]?.id) {
    return { ok: false, reason: 'Player already in this game' };
  }
  const nameOwner = findPlayerByName(players, newPlayer.name);
  if (nameOwner) {
    return { ok: false, reason: 'Name already taken. Please choose a different name.' };
  }
  if (occupancyCount(players) >= maxPlayers) {
    return { ok: false, reason: 'Room is full.' };
  }

  const player = {
    id: newPlayer.id,
    name: newPlayer.name,
    isHost: false,
    isApproved: true,
    stack: newPlayer.stack || 0,
    totalInvestment: newPlayer.totalInvestment || newPlayer.stack || 0,
    role: 'PLAYER',
    status: 'active',
    joinedAt: now || Date.now(),
    isActive: !playing,
    hasFolded: !!playing,
    isAllIn: false,
    hasActedThisRound: false,
    currentRoundBet: 0,
    totalPotContribution: 0,
  };

  const updated = clonePlayers(players);
  updated[player.id] = player;
  return { ok: true, players: updated, player };
}

/**
 * Table default buy-in in cents: the host's recorded buy-in
 * (`totalInvestment`), then their current stack, then $50.
 * Joiners copy this instead of typing chips. No new Firebase field.
 */
export function tableDefaultBuyIn(players, hostId) {
  const host = hostId ? players?.[hostId] : null;
  const cents = Number(host?.totalInvestment || host?.stack || 0);
  if (Number.isFinite(cents) && cents > 0) return Math.round(cents);
  return 5000;
}
